import type * as React from "react";
import { GearSixIcon, SignOutIcon, UserCircleIcon, UsersThreeIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";

import { Avatar, AvatarFallback, AvatarImage } from "@libs/zapi-ui/ds/components/avatar";
import { Badge } from "@libs/zapi-ui/ds/components/badge";
import { RichMenuItem, RichMenuLabel } from "./RichMenu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";

type Organization = {
  name: string;
  logoUrl?: string;
  memberCount?: number;
};

type OrganizationMember = {
  email: string;
};

type OrganizationMenuAction = "settings" | "team" | "profile" | "sign-out";

type OrganizationMenuLabels = {
  trigger: (organization: string) => string;
  organization: string;
  settings: string;
  settingsDescription: string;
  team: string;
  teamDescription: string;
  account: string;
  profile: string;
  signOut: string;
};

function initialsOf(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("");
}

function OrganizationAvatar({ organization, className }: { organization: Organization; className?: string }) {
  return (
    <Avatar className={cn("size-9 rounded-full", className)}>
      {organization.logoUrl && <AvatarImage src={organization.logoUrl} alt="" />}
      <AvatarFallback className="typo-label-sm bg-secondary-600 text-secondary-50 dark:bg-secondary-500">
        {initialsOf(organization.name)}
      </AvatarFallback>
    </Avatar>
  );
}

function OrganizationMenu({
  organization,
  member,
  showsOrganizationActions,
  labels,
  onAction,
  children,
  defaultOpen,
  modal
}: {
  organization: Organization;
  member?: OrganizationMember;
  showsOrganizationActions: boolean;
  labels: OrganizationMenuLabels;
  onAction?: (action: OrganizationMenuAction) => void;
  children?: React.ReactNode;
  defaultOpen?: boolean;
  modal?: boolean;
}) {
  return (
    <DropdownMenu defaultOpen={defaultOpen} modal={modal}>
      <DropdownMenuTrigger
        aria-label={labels.trigger(organization.name)}
        className="rounded-full outline-none transition-shadow focus-visible:ring-3 focus-visible:ring-ring/50 data-[popup-open]:ring-3 data-[popup-open]:ring-ring/30"
      >
        <OrganizationAvatar organization={organization} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={10} className="w-80 rounded-xl p-2 shadow-lg">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex items-center gap-3 rounded-lg bg-muted/60 p-3">
            <OrganizationAvatar organization={organization} className="size-11" />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="typo-label-lg truncate text-foreground">{organization.name}</span>
              {member && <span className="typo-caption truncate text-muted-foreground">{member.email}</span>}
            </span>
          </DropdownMenuLabel>
        </DropdownMenuGroup>

        {showsOrganizationActions && (
          <DropdownMenuGroup>
            <RichMenuLabel>{labels.organization}</RichMenuLabel>
            <RichMenuItem
              icon={GearSixIcon}
              title={labels.settings}
              description={labels.settingsDescription}
              onClick={() => onAction?.("settings")}
            />
            <RichMenuItem
              icon={UsersThreeIcon}
              title={labels.team}
              description={labels.teamDescription}
              trailing={
                organization.memberCount !== undefined && (
                  <Badge variant="secondary" className="tabular-nums">
                    {organization.memberCount}
                  </Badge>
                )
              }
              onClick={() => onAction?.("team")}
            />
          </DropdownMenuGroup>
        )}

        <DropdownMenuGroup>
          <RichMenuLabel>{labels.account}</RichMenuLabel>
          <RichMenuItem icon={UserCircleIcon} title={labels.profile} onClick={() => onAction?.("profile")} />
        </DropdownMenuGroup>

        {children}

        <DropdownMenuSeparator className="my-2" />
        <RichMenuItem icon={SignOutIcon} title={labels.signOut} onClick={() => onAction?.("sign-out")} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export {
  OrganizationMenu,
  OrganizationAvatar,
  type Organization,
  type OrganizationMember,
  type OrganizationMenuAction,
  type OrganizationMenuLabels
};
